import { createContext, useCallback, useContext, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { MessageSquareHeart } from "lucide-react";

const FeedbackGateContext = createContext(null);
export const useFeedbackGate = () => useContext(FeedbackGateContext);

export function FeedbackGateProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const resolverRef = useRef(null);

  // Wrap any PDF (or gated) action. Checks feedback requirement first; if required,
  // shows the mandatory form and only runs `fn` after a successful submission.
  const guard = useCallback(async (fn) => {
    let required = false;
    let activeForm = null;
    try {
      const { data } = await api.get("/feedback-form");
      required = !!data.required;
      activeForm = data.form;
    } catch {
      required = false;
    }
    if (!required) return fn();

    setForm(activeForm);
    setAnswers({});
    setOpen(true);
    await new Promise((resolve) => { resolverRef.current = resolve; });
    return fn();
  }, []);

  const setSingle = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));
  const toggleMulti = (qid, opt) => setAnswers((a) => {
    const cur = Array.isArray(a[qid]) ? a[qid] : [];
    return { ...a, [qid]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
  });

  const isEmpty = (v) => v == null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && v.length === 0);

  const submit = async () => {
    const missing = (form?.questions || []).find((q) => q.required && isEmpty(answers[q.id]));
    if (missing) { toast.error(`Falta responder: ${missing.label}`); return; }
    setSubmitting(true);
    try {
      const payload = { answers: (form?.questions || []).map((q) => ({ questionId: q.id, value: answers[q.id] ?? (q.type === "multi" ? [] : "") })) };
      await api.post("/feedback", payload);
      toast.success("¡Gracias por tu opinión!");
      setOpen(false);
      const resolve = resolverRef.current; resolverRef.current = null;
      if (resolve) resolve();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FeedbackGateContext.Provider value={{ guard }}>
      {children}
      <Dialog open={open} onOpenChange={() => { /* mandatory: cannot dismiss */ }}>
        <DialogContent
          className="rounded-none max-w-lg max-h-[88vh] overflow-y-auto"
          data-testid="feedback-modal"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          hideClose
        >
          <DialogHeader>
            <div className="flex items-center gap-2 brand-text">
              <MessageSquareHeart size={20} />
              <DialogTitle className="font-head">{form?.title || "Tu opinión nos importa"}</DialogTitle>
            </div>
            {form?.description ? <DialogDescription className="text-sm">{form.description}</DialogDescription> : null}
          </DialogHeader>

          <div className="space-y-5 py-2">
            {(form?.questions || []).map((q, idx) => (
              <div key={q.id} data-testid={`feedback-q-${q.id}`}>
                <Label className="text-sm font-medium text-slate-800">
                  {idx + 1}. {q.label} {q.required && <span className="text-destructive">*</span>}
                </Label>

                {q.type === "single" && (
                  <RadioGroup className="mt-2 space-y-1.5" value={answers[q.id] || ""} onValueChange={(v) => setSingle(q.id, v)}>
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <RadioGroupItem value={opt} id={`${q.id}-${oi}`} data-testid={`feedback-${q.id}-opt-${oi}`} />
                        <Label htmlFor={`${q.id}-${oi}`} className="text-sm font-normal cursor-pointer">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                {q.type === "multi" && (
                  <div className="mt-2 space-y-1.5">
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <Checkbox
                          id={`${q.id}-${oi}`}
                          checked={Array.isArray(answers[q.id]) && answers[q.id].includes(opt)}
                          onCheckedChange={() => toggleMulti(q.id, opt)}
                          data-testid={`feedback-${q.id}-opt-${oi}`}
                        />
                        <Label htmlFor={`${q.id}-${oi}`} className="text-sm font-normal cursor-pointer">{opt}</Label>
                      </div>
                    ))}
                  </div>
                )}

                {q.type === "text" && (
                  <Textarea
                    className="mt-2 rounded-none" rows={3}
                    value={answers[q.id] || ""} onChange={(e) => setSingle(q.id, e.target.value)}
                    data-testid={`feedback-${q.id}-text`}
                    placeholder="Escribí tu respuesta…"
                  />
                )}
              </div>
            ))}
          </div>

          <Button onClick={submit} disabled={submitting} data-testid="feedback-submit" className="rounded-none brand-bg text-white w-full">
            {submitting ? "Enviando…" : "Enviar y continuar"}
          </Button>
        </DialogContent>
      </Dialog>
    </FeedbackGateContext.Provider>
  );
}
